<script lang="ts" setup>
import { useStoreConfig } from '~/store/storeConfig'

const { isMobile } = useStoreConfig()
const scrollPosition = ref(0)

const props = defineProps<{
  visible: boolean
  header?: string
  footer?: string
  sizeSm?: boolean
  sizeMd?: boolean
  sizeXl?: boolean
  hideOnClickOutside?: boolean
  class?: string
  customHeader?: boolean
  isFullScreen?: boolean
}>()

const emit = defineEmits(['handle'])
const visible = ref(props.visible ?? false)

watch(
  () => props.visible,
  (value) => {
    if (typeof window !== 'undefined') {
      if (value) {
        scrollPosition.value = window?.scrollY ?? 0
      }

      lockScroll(value, scrollPosition.value ?? 0)

      if (!value) {
        scrollPosition.value = 0
      }

      visible.value = value
    }
  }
)

onUnmounted(() => {
  lockScroll(false)
})

function handle(value: boolean) {
  emit('handle', value)
}

const modalSizeClass = computed(() => {
  if (isMobile) {
    return 'modal-mobile'
  }

  if (props.sizeSm) {
    return 'modal-sm'
  }

  if (props.sizeMd) {
    return 'modal-md'
  }

  if (props.sizeXl) {
    return 'modal-xl'
  }

  return ''
})
</script>

<template>
  <UModal
    v-model="visible"
    class="modal-component"
    :hide-on-click-outside="props.hideOnClickOutside"
    :ui="{
      base: `${props.class} ${modalSizeClass} dark:shadow-lg dark:shadow-dmGray4 md:h-auto`,
      container: `modal-wrapper p-4 lg:p-0 !text-left items-center ${
        isFullScreen ? 'h-screen md:h-auto w-screen md:w-auto !p-0' : ''
      }`,
      wrapper: 'z-[1050]',
      overlay: {
        base: 'modal-overlay',
      },
    }"
    @update:model-value="handle">
    <UCard
      :ui="{
        base: `modal flex flex-col lg:!max-h-[90vh] ${
          isFullScreen ? 'h-full md:h-auto !rounded-none md:!rounded-sm' : ''
        }`,
        divide: 'divide-y divide-gray-100 dark:divide-gray-800',
        body: {
          base: `modal-body block overflow-auto relative `,
        },
        header: {
          padding: '0',
          base: `modal-header relative ${isFullScreen ? '!rounded-none md:!rounded-t-sm' : ''}`,
        },
        footer: {
          padding: '0',
          base: 'modal-footer',
        },
      }">
      <template v-if="props.header?.length || customHeader" #header>
        <div v-if="customHeader">
          <slot name="header" />
        </div>
        <div v-else>
          <h5 class="text-text1 dark:text-dmText1" v-html="props.header" />
        </div>
        <i class="fas fa-times absolute right-6 cursor-pointer" @click="handle(false)" />
      </template>

      <slot />

      <template v-if="props.footer?.length" #footer>{{ props.footer }}</template>
    </UCard>
  </UModal>
</template>

<style lang="postcss" scoped>
.modal {
  @apply dark:!ring-gray-700;
}
</style>
